import { Info } from 'lucide-react'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import type { PageBlockDefinition } from '../page-blocks/definition-types'
import {
  EDITORIAL_GRID_PRESET_CONFIG,
  type EditorialGridLayoutPreset,
} from '../page-blocks/definitions/editorialGrid'
import { PresetVisual } from '../editor-shared/PresetVisual'
import { COL_COLORS, COL_KEYS } from '../editor-shared/canvasTypes'
import type { CanvasSlot } from '../editor-shared/canvasTypes'
import type { BlockCanvasState } from '../editor-shared/useBlockCanvas'

// ─── EditorialGridShelf ───────────────────────────────────────────────────────

function EditorialGridShelf({ slot }: { slot: CanvasSlot }) {
  const preset = (slot.config.layoutPreset as EditorialGridLayoutPreset) ?? '3-equal'
  const presetCfg = EDITORIAL_GRID_PRESET_CONFIG[preset]
  const colKeys = COL_KEYS.slice(0, presetCfg.columns)
  const colLabels = ['Column 1', 'Column 2', 'Column 3'].slice(0, presetCfg.columns)

  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-semibold text-fg">Editorial Grid</div>
        <div className="mt-1 text-xs leading-5 text-fg-secondary">
          A {presetCfg.columns}-column row. Use "Add Block" in the canvas to stack multiple block types per column.
        </div>
      </div>
      <hr className="border-line" />
      <div>
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Layout Preset</div>
        <PresetVisual gridTemplate={presetCfg.gridTemplate} />
        <div className="text-xs font-semibold text-fg">{presetCfg.label}</div>
        <div className="mt-0.5 text-[10px] text-fg-secondary">{presetCfg.gridTemplate}</div>
      </div>
      <hr className="border-line" />
      <div>
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Column Assignments</div>
        <div className="space-y-3">
          {colKeys.map((colKey, i) => {
            const blocks = slot.columnBlocks?.[colKey] ?? []
            const color = COL_COLORS[i]
            return (
              <div key={colKey}>
                <span className={`flex items-center gap-1.5 text-[10px] font-semibold ${color.label}`}>
                  <span className={`inline-block h-2 w-2 rounded-full ${color.dot}`} />
                  {colLabels[i]}
                </span>
                {blocks.length === 0 ? (
                  <span className="mt-1 block text-[10px] italic text-fg-secondary opacity-60">Empty</span>
                ) : (
                  <div className="mt-1 space-y-0.5">
                    {blocks.map((b) => (
                      <div key={b.id} className="rounded bg-fill px-2 py-0.5 text-[10px] font-semibold text-fg">
                        {PAGE_BLOCK_REGISTRY[b.type]?.label ?? b.type}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── BlockDetailShelf ─────────────────────────────────────────────────────────

function BlockDetailShelf({ def }: { def: PageBlockDefinition }) {
  return (
    <div className="space-y-5">

      {/* Identity */}
      <div>
        <div className="text-sm font-semibold text-fg">{def.label}</div>
        <div className="mt-1 text-xs leading-5 text-fg-secondary">{def.description}</div>
      </div>

      <hr className="border-line" />

      {/* Visual options */}
      <div>
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Visual Options</div>
        <div className="space-y-2">
          {def.editorFields.map((field) => (
            <div key={field.key} className="text-xs">
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-fg">{field.label}</span>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-fg-secondary">
                  {field.type === 'toggle' ? 'on / off' : field.type}
                </span>
              </div>
              {field.options && field.options.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {field.options.map((opt) => (
                    <span
                      key={opt.value}
                      className="rounded border border-line bg-canvas px-1.5 py-0.5 text-[10px] text-fg-secondary"
                    >
                      {opt.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Named slots — only for multi-slotted blocks */}
      {def.slots.length > 0 && (
        <>
          <hr className="border-line" />
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Named Slots</div>
            <div className="space-y-1.5">
              {def.slots.map((slot) => (
                <div key={slot.name} className="flex items-center justify-between text-xs">
                  <span className="text-fg">{slot.label}</span>
                  <span
                    className={`text-[10px] uppercase tracking-wide ${slot.required ? 'text-fg' : 'text-fg-secondary'}`}
                  >
                    {slot.required ? 'Required' : 'Optional'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {def.category === 'editorial' && (
        <>
          <hr className="border-line" />

          {/* Content source */}
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Content Source</div>
            <div className="space-y-2 text-xs leading-5 text-fg-secondary">
              <p>
                <span className="font-semibold text-fg">Static — </span>
                page editor hand-picks{' '}
                {def.minArticles === def.maxArticles
                  ? `exactly ${def.minArticles}`
                  : `${def.minArticles}–${def.maxArticles}`}{' '}
                article{def.maxArticles !== 1 ? 's' : ''} in order.
              </p>
              <p>
                <span className="font-semibold text-fg">Dynamic — </span>
                page editor selects a feed; articles are resolved at render time
                {def.maxArticles < 24 ? ` (up to ${def.maxArticles})` : ''}.
              </p>
            </div>
          </div>
        </>
      )}

    </div>
  )
}

// ─── LayoutRightShelf ───────────────────────────────────────────────────────────────

interface LayoutRightShelfProps {
  canvas: BlockCanvasState
}

export function LayoutRightShelf({ canvas }: LayoutRightShelfProps) {
  const { selectedSlot, selectedColumnType, setSelectedColumnType, setSelectedColKey, setSelectedColBlockId } = canvas

  const selectedDef = selectedColumnType
    ? (PAGE_BLOCK_REGISTRY[selectedColumnType] ?? null)
    : selectedSlot
      ? (PAGE_BLOCK_REGISTRY[selectedSlot.type] ?? null)
      : null

  return (
    <aside className="w-72 shrink-0 overflow-y-auto border-l border-line bg-shelf p-4">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="type-section-label text-accent-tertiary-fg">Block Reference</h2>
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-3 px-2 py-0.5 text-[10px] font-semibold text-blue-11">
          <Info size={10} aria-hidden="true" />
          Read only
        </span>
      </div>
      <p className="mb-4 text-[10px] leading-4 text-fg-secondary">
        Information only. Visual and content options are configured when creating a page from this layout.
      </p>

      {!selectedSlot && (
        <p className="text-xs text-fg-secondary">Select a block in the canvas to see details.</p>
      )}

      {selectedSlot?.type === 'editorial-grid' && !selectedColumnType && (
        <EditorialGridShelf slot={selectedSlot} />
      )}

      {selectedSlot?.type === 'editorial-grid' && selectedColumnType && selectedDef && (
        <>
          <button
            type="button"
            onClick={() => {
              setSelectedColumnType(null)
              setSelectedColKey(null)
              setSelectedColBlockId(null)
            }}
            className="mb-4 flex items-center gap-1 text-[10px] font-semibold text-fg-secondary hover:text-fg"
          >
            ← Editorial Grid
          </button>
          <BlockDetailShelf def={selectedDef} />
        </>
      )}

      {selectedSlot && selectedSlot.type !== 'editorial-grid' && selectedDef && (
        <BlockDetailShelf def={selectedDef} />
      )}
    </aside>
  )
}
